import type { Session, User } from "@supabase/supabase-js"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import { setAccessToken } from "./auth-store"
import { getSupabase } from "./supabase"

type AuthContextValue = {
  session: Session | null
  user: User | null
  initialized: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    const sb = getSupabase()
    if (!sb) {
      setInitialized(true)
      return
    }
    let cancelled = false
    void sb.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setSession(data.session)
      setAccessToken(data.session?.access_token ?? null)
      setInitialized(true)
    })
    const { data: sub } = sb.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      setAccessToken(next?.access_token ?? null)
      setInitialized(true)
    })
    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const sb = getSupabase()
    if (!sb) return { error: "Supabase is not configured." }
    const { error } = await sb.auth.signInWithPassword({ email, password })
    return { error: error?.message ?? null }
  }, [])

  const signOut = useCallback(async () => {
    const sb = getSupabase()
    if (sb) await sb.auth.signOut()
    setAccessToken(null)
    setSession(null)
  }, [])

  const value = useMemo(
    () => ({
      initialized,
      session,
      signIn,
      signOut,
      user: session?.user ?? null,
    }),
    [initialized, session, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
